import { useNavigate } from "react-router"
import { useAuth } from "../auth/use-auth"
import EnkIwpLogo from "../components/iwp-logo"
import { useLocation } from "react-router"
import { useState } from "react"
import { TEnkLoginResult, TEnkMFAMethode, EnkIWPServerAuth as ApiAuth } from "../api/api-iwp-server"
import { loginResultToUser } from "../api/api-utils"
import { EnkHeader } from "../components/text/header"
import { EnkLinkLabel } from "../components/text/link-label"
import { EnkInputField } from "../components/inputs/inputfield"
import { EnkInput } from "../components/inputs/input"
import { EnkPar } from "../components/text/par"
import { EnkPrimaryButton } from "../components/common/buttons"
import EnkMfaDialog from "../dialogs/mfa-dialog"
import { EnkApiError } from "../api/do-request"
import { useMsgDialog } from "../dialogs/use-msgdialog"

const CHECKBOX_VALUE = "on"

export default function EnkLogin() {
  const [mfaDlgOpen, setMfaDlgOpen] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<string>("")
  const [mfaRequired, setMfaRequired] = useState<boolean>(false)
  const [mfaMethod, setMfaMethod] = useState<TEnkMFAMethode>("none")
  const [mfaMethods, setMfaMethods] = useState<TEnkMFAMethode[]>([])
  const [mfaCode, setMfaCode] = useState<string>("")
  const [rememberMe, setRememberMe] = useState<boolean>(false)
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const { messageDialog } = useMsgDialog()
  const { onLoggedIn } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  interface LocationState {
    fromUrl?: string
  }

  const state = location.state as LocationState
  const fromUrl = state?.fromUrl || "/"

  //do login
  const callLogin = async (email: string, password: string, mfaCode: string, rememberMe: boolean) => {
    try {
      const loginResult = await ApiAuth.login(email, password, rememberMe, mfaCode)
      //if mfa setup is required we go in to the mfa setup dialog
      if (loginResult.mfaSetupRequired) {
        setLoginError("")
        setMfaMethods(loginResult.mfaSetupKinds)
        console.log("Required mfa methods:", loginResult.mfaSetupKinds)
        setMfaDlgOpen(true)
      } else if (loginResult.mfaNeededWithMethod && loginResult.mfaNeededWithMethod !== "none") {
        setLoginError("")
        setMfaRequired(true)
        setMfaMethod(loginResult.mfaNeededWithMethod as TEnkMFAMethode)
      } else {
        if (loginResult.user) {
          onLoggedIn(loginResultToUser(loginResult.user), rememberMe)
          await navigate(fromUrl, { replace: true })
        } else {
          throw new Error("User not found")
        }
      }
    } catch (error: unknown) {
      const reason = error instanceof EnkApiError ? error.description : "Onbekende fout opgetreden bij het inloggen!"
      console.error("Login failed:", error, reason)
      setLoginError(reason)
    }
  }

  //handle login submit
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)
    const email = formData.get("email") as string
    const password = formData.get("password") as string
    const rememberMe = (formData.get("remember-me") as string) === CHECKBOX_VALUE

    setEmail(email)
    setPassword(password)
    setRememberMe(rememberMe)
    await callLogin(email, password, "", rememberMe)
  }

  // Handle MFA form submit
  const handleMfaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await callLogin(email, password, mfaCode, rememberMe)
  }

  const handleLoggedIn = (user: TEnkLoginResult) => {
    if (!user.user) {
      console.error("No user info after login")
      setLoginError("Geen gebruikersinformatie ontvangen na inloggen!")
      return
    }
    onLoggedIn(loginResultToUser(user.user), rememberMe)
    const navigationResult = navigate(fromUrl, { replace: true })
    if (navigationResult instanceof Promise) {
      navigationResult.catch((error: unknown) => {
        console.error("Failed to navigate:", error)
      })
    }
  }

  const handlePasswordReset = async () => {
    //call api to send password reset email
    try {
      const emailInput = document.getElementById("email") as HTMLInputElement
      const emailValue = emailInput?.value

      if (!emailValue) {
        setLoginError("Email adres is verplicht.")
        return
      }

      await ApiAuth.sendPasswordResetMail(emailValue, "")
      messageDialog(
        "info",
        `Een e-mail met instructies voor het opnieuw instellen van uw wachtwoord is verzonden naar "${emailValue}".`,
        ["OK"],
        () => {}
      )
    } catch (error: unknown) {
      const reason = error instanceof EnkApiError ? error.description : "Onbekende fout opgetreden bij het verzenden van de e-mail!"
      console.error("Password reset failed:", error, reason)
      messageDialog("error", "Fout bij het verzenden van verzoek tot wachtwoord reset: " + reason, ["OK"], () => {})
    }
  }

  return (
    <div className="flex h-full flex-1">
      <EnkMfaDialog
        open={mfaDlgOpen}
        setOpen={setMfaDlgOpen}
        emailaddress={email}
        password={password}
        rememberMe={rememberMe}
        mfaMethods={mfaMethods}
        onLoggedIn={handleLoggedIn}
      />
      <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {!mfaRequired ? (
            <>
              <div>
                <EnkIwpLogo />
                <EnkHeader text="Inloggen met je account" />
                {/* <EnkPar>
                  Nog geen account? <EnkLinkLabel href="#" text="Vraag een account aan" />
                </EnkPar> */}
              </div>

              <div className="mt-10">
                <div>
                  {loginError && <p className="mt-2 text-sm leading-6 text-red-500">{loginError}</p>}

                  <form
                    onSubmit={(e) => {
                      void handleFormSubmit(e)
                    }}
                    method="POST"
                    className="space-y-6"
                  >
                    <EnkInputField label="Email adres" className="">
                      <EnkInput id="email" type="email" autoComplete="email" required />
                    </EnkInputField>

                    <EnkInputField label="Wachtwoord" className="">
                      <EnkInput id="password" type="password" autoComplete="current-password" required />
                    </EnkInputField>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          id="remember-me"
                          name="remember-me"
                          type="checkbox"
                          value={CHECKBOX_VALUE}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <label htmlFor="remember-me" className="ml-3 block text-sm leading-6 text-gray-700">
                          onthoud mij
                        </label>
                      </div>

                      <div className="text-sm leading-6">
                        <EnkLinkLabel
                          text="Wachtwoord vergeten?"
                          onClick={() => {
                            void handlePasswordReset()
                          }}
                        />
                      </div>
                    </div>

                    <EnkPrimaryButton text="Aanmelden" type="submit" fullWidth />
                  </form>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <EnkIwpLogo />
                <EnkHeader text="Authorisatiecode" />
                {mfaMethod === "app" && <EnkPar>Geef hier uw authorisatiecode op die u kunt vinden in uw Google authenticatie app.</EnkPar>}
                {mfaMethod === "email" && <EnkPar>Geef hier uw verificatiecode op die naar je gemaild is.</EnkPar>}
                {mfaMethod === "sms" && <EnkPar>Geef hier uw verificatiecode op die naar je mobiel gestuurd is.</EnkPar>}
              </div>

              <div className="mt-10">
                <div>
                  {loginError && <p className="mt-2 text-sm leading-6 text-gray-500">{loginError}</p>}

                  <form
                    onSubmit={(e) => {
                      void handleMfaSubmit(e)
                    }}
                    className="space-y-6"
                  >
                    <EnkInputField label="Authorisatiecode" className="">
                      <EnkInput
                        id="mfa-code"
                        type="text"
                        autoComplete="one-time-code"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        required
                      />
                    </EnkInputField>

                    <EnkPrimaryButton text="Aanmelden" fullWidth type="submit" />
                  </form>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="relative hidden w-0 flex-1 lg:block">
        <img alt="" src="/iwp-login-img.svg" className="absolute inset-0 size-full object-cover" />
      </div>
    </div>
  )
}
